import React from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  styled,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import LocalHospital from '@mui/icons-material/LocalHospital'
import { default as AssessmentIcon } from '@mui/icons-material/Assessment'
import Notes from '@mui/icons-material/Notes'
import Attachment from '@mui/icons-material/Attachment'
import DiagnosisSelectMenu from 'src/mentor/components/PrescriptionMakingPage/inputComponents/diagnosis/DiagnosisMenu'
import { SuggestedAssessmentsSelectMenu } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid rgba(67, 56, 202, 0.15)',
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.02) 0%, rgba(99, 102, 241, 0.02) 100%)',
  marginBottom: '16px',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(67, 56, 202, 0.3)',
    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.08)',
  },
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#4338ca',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
}))

// Fix component to render as div to avoid invalid DOM nesting
SectionTitle.defaultProps = {
  component: 'div',
}

const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: 'rgba(0, 0, 0, 0.6)',
  marginTop: '4px',
  fontStyle: 'italic',
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: '#fff',
    '&:hover fieldset': {
      borderColor: 'rgba(67, 56, 202, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4338ca',
    },
  },
}))

const RequiredBadge = styled(Chip)(({ theme }) => ({
  height: '20px',
  fontSize: '10px',
  fontWeight: 600,
  background: 'rgba(239, 68, 68, 0.1)',
  color: '#dc2626',
  border: '1px solid rgba(239, 68, 68, 0.2)',
}))

export default function ModernSendAssessmentComponent({
  clinicId,
  clinicBrandId,
  selectedDiagnosis,
  setSelectedDiagnosis,
  selectedAssessments,
  setSelectedAssessments,
  notesForAssessments,
  setNotesForAssessments,
  attachPrescriptionForAssessment,
  setAttachPrescriptionForAssessment,
  loading,
  setLoading,
  mentorProfileData,
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Diagnosis Section - Shared across both tabs */}
      <ModernCard>
        <CardContent>
          <SectionHeader>
            <SectionTitle>
              <LocalHospital sx={{ fontSize: 18 }} />
              Diagnosis
              <RequiredBadge label="Required" size="small" />
            </SectionTitle>
          </SectionHeader>
          <HelperText>
            Select the primary diagnosis and any additional conditions (shared with Therapy Referral)
          </HelperText>
          <Box sx={{ marginTop: 2 }}>
            <DiagnosisSelectMenu
              clinicId={clinicId}
              clinicBrandId={clinicBrandId}
              selectedDiagnosis={selectedDiagnosis}
              setSelectedDiagnosis={setSelectedDiagnosis}
              menuPortalTarget={document.body}
              maxZIndex
              isLoading={loading}
            />
          </Box>
        </CardContent>
      </ModernCard>

      {/* Assessments Section */}
      <ModernCard>
        <CardContent>
          <SectionHeader>
            <SectionTitle>
              <AssessmentIcon sx={{ fontSize: 18 }} />
              Psychological Assessments
              <RequiredBadge label="Required" size="small" />
            </SectionTitle>
          </SectionHeader>
          <HelperText>
            Select the psychological assessments needed for this patient
          </HelperText>
          <Box sx={{ marginTop: 2 }}>
            <SuggestedAssessmentsSelectMenu
              clinicId={clinicId}
              clinicBrandId={clinicBrandId}
              selectedAssessments={selectedAssessments}
              setSelectedAssessments={setSelectedAssessments}
              menuPortalTarget={document.body}
              maxZIndex
              doctorSpecializations={mentorProfileData?.specializedCategories}
            />
          </Box>
        </CardContent>
      </ModernCard>

      {/* Notes Section - Only show if assessments are selected */}
      {selectedAssessments && selectedAssessments.length > 0 && (
        <ModernCard>
          <CardContent>
            <SectionHeader>
              <SectionTitle>
                <Notes sx={{ fontSize: 18 }} />
                Notes for Psychologist
              </SectionTitle>
            </SectionHeader>
            <HelperText>
              Provide any additional context or specific instructions for the assessment
            </HelperText>
            <StyledTextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter notes for the psychologists who will conduct the assessment..."
              value={notesForAssessments || ''}
              onChange={(e) => setNotesForAssessments(e.target.value)}
              sx={{ marginTop: 2 }}
            />
          </CardContent>
        </ModernCard>
      )}

      {/* Attach Prescription Checkbox */}
      <ModernCard>
        <CardContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={attachPrescriptionForAssessment || false}
                onChange={(e) => setAttachPrescriptionForAssessment(e.target.checked)}
                sx={{
                  color: '#4338ca',
                  '&.Mui-checked': {
                    color: '#4338ca',
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Attachment sx={{ fontSize: 18, color: '#4338ca' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4338ca' }}>
                    Attach Prescription/Report
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                    Send the prescription report to the psychologist who receives this referral
                  </Typography>
                </Box>
              </Box>
            }
          />
        </CardContent>
      </ModernCard>
    </Box>
  )
}

